import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { GraduationCap, BookOpen, Briefcase, Building2, Code2, FlaskConical, BriefcaseBusiness, ArrowRight } from 'lucide-react';
import ProgramDetail from './ProgramDetail';

const tabs = [
  {
    id: 'institutions',
    label: 'Institutions & Colleges',
    emoji: '🏫',
    tagline: 'Industry-grade academic programmes for colleges & universities',
    gradient: 'from-brand-blue via-blue-600 to-indigo-700',
    accent: '#1A3BCC',
    programs: [
      {
        icon: <GraduationCap size={24} />,
        title: 'Student Development Programme',
        short: 'SDP',
        desc: 'Intensive, hands-on programmes that bridge academic learning and industry requirements. Students gain real project experience under expert mentorship.',
        features: ['Live project exposure', 'Industry mentorship', 'Certifications awarded', 'Placement assistance'],
        color: 'from-brand-blue to-blue-700', accent: '#1A3BCC', featured: true,
      },
      {
        icon: <BookOpen size={24} />,
        title: 'Faculty Development Programme',
        short: 'FDP',
        desc: 'Equip educators with the latest industry knowledge and pedagogical tools to design modern, relevant curricula.',
        features: ['Technology workshops', 'Curriculum design support', 'Industry collaboration', 'Research guidance'],
        color: 'from-orange-500 to-orange-600', accent: '#FF7B00',
      },
      {
        icon: <Building2 size={24} />,
        title: 'College Collaboration (MoU)',
        short: 'MOU',
        desc: 'Formal MoU partnerships to co-deliver training, run campus hackathons, and support institutional placement drives.',
        features: ['MoU with institutions', 'Campus hackathons', 'Tech symposiums', 'Placement drives'],
        color: 'from-violet-500 to-purple-700', accent: '#7C3AED',
      },
    ],
  },
  {
    id: 'students',
    label: 'Students',
    emoji: '🎓',
    tagline: 'Real-world exposure, mentorship and research support for your growth',
    gradient: 'from-sky-500 via-cyan-500 to-teal-500',
    accent: '#0EA5E9',
    programs: [
      {
        icon: <Briefcase size={24} />,
        title: 'Internship Programme',
        short: 'INTERN',
        desc: 'Work on live client projects across 6 technology tracks. Collaborate with real teams, get weekly feedback, and earn an industry-recognised certificate.',
        features: ['6 technology tracks', 'Real client projects', 'Weekly evaluations', 'Stipend for top performers'],
        color: 'from-sky-400 to-cyan-600', accent: '#0EA5E9', featured: true,
      },
      {
        icon: <FlaskConical size={24} />,
        title: 'Research Support',
        short: 'RESEARCH',
        desc: 'Technical and conceptual support for students engaged in advanced research — from algorithm implementation to paper publication.',
        features: ['Implementation assistance', 'Publication guidance', 'Data modelling', 'Patent support'],
        color: 'from-emerald-400 to-teal-600', accent: '#10B981',
      },
    ],
  },
  {
    id: 'corporate',
    label: 'Corporate',
    emoji: '🏢',
    tagline: 'Custom technology solutions and pre-vetted talent for your enterprise',
    gradient: 'from-rose-500 via-red-500 to-orange-500',
    accent: '#E11D48',
    programs: [
      {
        icon: <Code2 size={24} />,
        title: 'Software Solutions',
        short: 'DEV',
        desc: 'Bespoke web, mobile, and AI solutions engineered to streamline your operations and power your digital transformation.',
        features: ['Custom Web & Mobile Apps', 'Enterprise AI Integration', 'Cloud Infrastructure', 'Dedicated Tech Teams'],
        color: 'from-rose-400 to-red-600', accent: '#E11D48', featured: true,
      },
      {
        icon: <BriefcaseBusiness size={24} />,
        title: 'Placement Assistance',
        short: 'HIRE',
        desc: 'Access pre-vetted, industry-ready talent from our training pipelines — matched precisely to your technical requirements.',
        features: ['Pre-vetted candidates', 'Custom hiring drives', 'Zero agency fees', 'Skill-matched profiles'],
        color: 'from-amber-400 to-yellow-600', accent: '#F59E0B',
      },
    ],
  },
];

function ProgramCard({ program, index, onOpen }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 28 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: index * 0.1, ease: [0.25, 0.46, 0.45, 0.94] }}
      onClick={() => onOpen(program)}
      className="group relative cursor-pointer rounded-3xl overflow-hidden border border-slate-200 bg-white shadow-card hover:shadow-[0_8px_40px_rgba(0,0,0,0.10)] hover:-translate-y-1 transition-all duration-300"
    >
      <div className={`absolute left-0 top-0 bottom-0 w-1.5 bg-gradient-to-b ${program.color}`} />

      <div className="p-7 pl-9">
        <div className="flex items-start justify-between mb-5">
          <div
            className="w-12 h-12 rounded-2xl flex items-center justify-center shadow-sm"
            style={{ backgroundColor: program.accent + '18', color: program.accent }}
          >
            {program.icon}
          </div>
          <span
            className="px-3 py-1 rounded-full text-[10px] font-space font-bold tracking-widest uppercase"
            style={{ backgroundColor: program.accent + '12', color: program.accent }}
          >
            {program.short}
          </span>
        </div>

        <h3 className="font-syne font-bold text-xl text-slate-900 mb-2 leading-snug group-hover:text-brand-blue transition-colors duration-200">
          {program.title}
        </h3>
        <p className="font-inter text-slate-500 text-sm leading-relaxed mb-5 line-clamp-3">
          {program.desc}
        </p>

        <div className="grid grid-cols-2 gap-x-3 gap-y-2 mb-6">
          {program.features.map((f, i) => (
            <div key={i} className="flex items-center gap-2">
              <div className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: program.accent }} />
              <span className="font-inter text-xs text-slate-600 truncate">{f}</span>
            </div>
          ))}
        </div>

        <div
          className="flex items-center gap-2 text-sm font-space font-semibold group-hover:gap-3 transition-all duration-200"
          style={{ color: program.accent }}
        >
          Explore Programme
          <svg width="14" height="14" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.5">
            <path strokeLinecap="round" strokeLinejoin="round" d="M9 18l6-6-6-6" />
          </svg>
        </div>
      </div>
    </motion.div>
  );
}

export default function Training() {
  const [activeTab, setActiveTab] = useState(0);
  const [selectedProgram, setSelectedProgram] = useState(null);
  const tab = tabs[activeTab];

  return (
    <>
      {selectedProgram && (
        <ProgramDetail program={selectedProgram} onClose={() => setSelectedProgram(null)} />
      )}

      <section id="training" className="py-16 relative overflow-hidden bg-slate-50">
        <div className="absolute inset-0 grid-pattern opacity-40 pointer-events-none" />
        <div className="absolute top-0 right-0 w-[600px] h-[500px] rounded-full bg-brand-blue/4 blur-3xl pointer-events-none" />
        <div className="absolute bottom-0 left-0 w-[400px] h-[400px] rounded-full bg-sky-400/5 blur-3xl pointer-events-none" />

        <div className="max-w-7xl mx-auto px-6 relative z-10">

          <motion.div
            initial={{ y: 30, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: true }}
            className="text-center mb-8"
          >
            <span className="section-label mb-5 inline-block">Training &amp; Education</span>
            <h2 className="font-syne font-extrabold text-4xl md:text-5xl text-slate-900 mb-4">
              Programmes for <span className="gradient-text-blue">Every Stakeholder</span>
            </h2>
            <p className="font-inter text-slate-500 text-lg max-w-2xl mx-auto leading-relaxed">
              Whether you are an institution, a student, or a corporate — QuBright has a tailored
              programme designed to deliver real, measurable outcomes.
            </p>
          </motion.div>

          <motion.div
            initial={{ y: 20, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: true }}
            transition={{ delay: 0.15 }}
            className="flex justify-center mb-6 w-full"
          >
            <div className="inline-flex max-w-full overflow-x-auto p-1.5 rounded-2xl bg-white border border-slate-200 shadow-card gap-1" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
              {/* Webkit scrollbar hiding is best done in css, but inline style works for standard */}
              {tabs.map((t, i) => (
                <button
                  key={t.id}
                  onClick={() => setActiveTab(i)}
                  className={`relative flex items-center gap-2.5 px-5 py-3 rounded-xl font-space font-semibold text-sm transition-all duration-300 whitespace-nowrap
                    ${activeTab === i ? 'text-white shadow-lg' : 'text-slate-500 hover:text-slate-800 hover:bg-slate-50'}`}
                >
                  {activeTab === i && (
                    <motion.div
                      layoutId="tab-bg"
                      className={`absolute inset-0 rounded-xl bg-gradient-to-r ${t.gradient}`}
                      transition={{ type: 'spring', stiffness: 400, damping: 36 }}
                    />
                  )}
                  <span className="relative z-10">{t.emoji}</span>
                  <span className="relative z-10">{t.label}</span>
                </button>
              ))}
            </div>
          </motion.div>

          <AnimatePresence mode="wait">
            <motion.div
              key={activeTab}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -12 }}
              transition={{ duration: 0.38, ease: [0.25, 0.46, 0.45, 0.94] }}
            >
              <div className="flex items-center gap-4 mb-5">
                <div className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: tab.accent }} />
                <p className="font-inter text-slate-600 text-base">{tab.tagline}</p>
                <div className="flex-1 h-px bg-slate-200" />
                <span className="font-space text-xs text-slate-400 tracking-widest uppercase">
                  {tab.programs.length} programme{tab.programs.length > 1 ? 's' : ''}
                </span>
              </div>

              <div className={`grid gap-5 ${tab.programs.length === 3 ? 'grid-cols-1 md:grid-cols-3' : 'grid-cols-1 md:grid-cols-2'}`}>
                {tab.programs.map((p, i) => (
                  <ProgramCard key={p.title} program={p} index={i} onOpen={setSelectedProgram} />
                ))}
              </div>
            </motion.div>
          </AnimatePresence>

          <motion.div
            initial={{ y: 30, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: true }}
            transition={{ delay: 0.25 }}
            className="mt-10 rounded-3xl overflow-hidden relative"
          >
            <div className="absolute inset-0 bg-gradient-to-r from-brand-blue via-blue-600 to-indigo-700" />
            <div className="absolute inset-0 grid-pattern opacity-10" />
            <div className="relative z-10 p-10 flex flex-col md:flex-row items-center justify-between gap-6">
              <div>
                <p className="font-space text-white/60 text-xs tracking-widest uppercase mb-2">For Colleges &amp; Universities</p>
                <h3 className="font-syne font-bold text-2xl text-white mb-2">Partner with QuBright</h3>
                <p className="font-inter text-blue-200 text-sm max-w-lg leading-relaxed">
                  Sign an MoU and bring industry-grade training, live hackathons, and campus
                  placement drives directly to your students — at zero cost.
                </p>
              </div>
              <div className="shrink-0 flex gap-3">
                <a href="#contact">
                  <button className="btn-orange whitespace-nowrap">Request Partnership</button>
                </a>
                <a href="#contact">
                  <button className="flex items-center gap-2 px-6 py-3 rounded-2xl bg-white/10 hover:bg-white/20 border border-white/20 text-white font-space font-semibold text-sm transition-all duration-300 whitespace-nowrap">
                    Learn More
                    <svg width="14" height="14" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.5">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M9 18l6-6-6-6" />
                    </svg>
                  </button>
                </a>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
    </>
  );
}
